/**
 * Message List Component
 * Scrollable list of messages with infinite scroll support
 * Loads latest messages first, older messages on scroll up
 */

import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  useMessagesInfinite,
  flattenInfiniteMessages,
  hasMoreOlderMessages,
} from '@/hooks/useMessages';
import { useScrollToLoadMore } from '@/hooks/useScrollToLoadMore';
import { useDMThread } from '@/hooks/useDMs';
import { useAppStore } from '@/store/app.store';
import { useAuthStore } from '@/store/auth.store';
import MessageItem from './MessageItem';
import PinnedMessagesPanel from './PinnedMessagesPanel';
import type { Message } from '@/types/api.types';

interface MessageListProps {
  onReply?: (message: Message) => void;
}

/**
 * Loading spinner for infinite scroll
 */
const LoadingSpinner = () => (
  <div className="flex justify-center py-4">
    <div className="flex items-center space-x-2 text-gray-500 dark:text-dark-text-muted">
      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="text-sm">Зареждане на съобщения...</span>
    </div>
  </div>
);

/**
 * Skeleton loader for initial load
 */
const MessageSkeleton = () => (
  <div className="flex-1 overflow-y-auto px-6 py-4">
    <div className="space-y-4 max-w-[960px] mx-auto w-full">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-start space-x-3 py-2 animate-pulse">
        <div className="w-10 h-10 bg-gray-200 dark:bg-dark-bg-input rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 dark:bg-dark-bg-input rounded w-1/4" />
          <div className="h-4 bg-gray-200 dark:bg-dark-bg-input rounded w-3/4" />
        </div>
      </div>
    ))}
    </div>
  </div>
);

const MessageList = ({ onReply }: MessageListProps) => {
  const { activeChannelId, activeDMThreadId } = useAppStore();
  const { user: currentUser } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const unreadDividerRef = useRef<HTMLDivElement>(null);
  const prevMessagesCountRef = useRef(0);
  const initialScrollDoneRef = useRef(false);

  // DM thread data — used to derive the first-unread boundary from the current
  // user's last_read_at. Channels do not expose last_read_at to the client, so
  // the divider is only available for DMs (see report).
  const { data: dmThread } = useDMThread(activeDMThreadId);

  // Freeze the last_read_at value at the moment we enter a conversation, so the
  // divider position stays stable even after the auto mark-as-read fires.
  const conversationKey = activeDMThreadId
    ? `dm:${activeDMThreadId}`
    : activeChannelId
      ? `ch:${activeChannelId}`
      : '';
  const frozenReadRef = useRef<{ key: string; lastReadAt: string | null; set: boolean }>({
    key: '',
    lastReadAt: null,
    set: false,
  });
  const [boundaryVersion, setBoundaryVersion] = useState(0);

  // Reset the frozen boundary whenever the active conversation changes.
  useEffect(() => {
    frozenReadRef.current = { key: conversationKey, lastReadAt: null, set: false };
    setBoundaryVersion((v) => v + 1);
  }, [conversationKey]);

  // Freeze the current user's last_read_at once the DM thread data is available.
  useEffect(() => {
    if (!activeDMThreadId || !dmThread) return;
    if (frozenReadRef.current.key !== conversationKey || frozenReadRef.current.set) return;
    const me = dmThread.members?.find((m) => m.id === currentUser?.id);
    frozenReadRef.current.lastReadAt = me?.last_read_at ?? null;
    frozenReadRef.current.set = true;
    setBoundaryVersion((v) => v + 1);
  }, [dmThread, activeDMThreadId, conversationKey, currentUser?.id]);

  // Use infinite query for message loading
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useMessagesInfinite(activeChannelId, activeDMThreadId);

  // Flatten pages into chronological message array
  const messages = flattenInfiniteMessages(data);
  const hasMore = hasMoreOlderMessages(data);

  // First unread message id (boundary for the "Нови съобщения" divider).
  // Derived from the frozen last_read_at; the first message from another user
  // newer than that timestamp marks the boundary. Null when unavailable.
  const firstUnreadMessageId = useMemo(() => {
    const lastReadAt =
      frozenReadRef.current.key === conversationKey ? frozenReadRef.current.lastReadAt : null;
    if (!lastReadAt || messages.length === 0) return null;
    const lastReadTime = new Date(lastReadAt).getTime();
    const boundary = messages.find(
      (m) => new Date(m.created_at).getTime() > lastReadTime && m.user_id !== currentUser?.id
    );
    return boundary?.id ?? null;
    // boundaryVersion forces recompute when the frozen ref is updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, conversationKey, currentUser?.id, boundaryVersion]);

  const jumpToUnread = useCallback(() => {
    unreadDividerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // Handle loading more messages with scroll position preservation
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Scroll detection hook
  const {
    scrollRef,
    saveScrollPosition,
    restoreScrollPosition,
    isAtBottom,
    scrollToBottom,
  } = useScrollToLoadMore({
    threshold: 300,
    enabled: true,
    onLoadMore: handleLoadMore,
    isLoading: isFetchingNextPage,
    hasMore: hasNextPage ?? hasMore,
  });

  // Reset scroll state when conversation changes (must be defined before scroll effects)
  useEffect(() => {
    initialScrollDoneRef.current = false;
    prevMessagesCountRef.current = 0;
  }, [activeChannelId, activeDMThreadId]);

  // Save scroll position before fetching more pages
  useEffect(() => {
    if (isFetchingNextPage) {
      saveScrollPosition();
    }
  }, [isFetchingNextPage, saveScrollPosition]);

  // Restore scroll position after new messages are prepended
  useEffect(() => {
    if (!isFetchingNextPage && messages.length > prevMessagesCountRef.current) {
      // Only restore if we added messages at the top (older messages)
      if (prevMessagesCountRef.current > 0) {
        restoreScrollPosition();
      }
    }
    prevMessagesCountRef.current = messages.length;
  }, [messages.length, isFetchingNextPage, restoreScrollPosition]);

  // Initial scroll to bottom on first load or conversation switch
  useEffect(() => {
    if (messages.length > 0 && !initialScrollDoneRef.current) {
      scrollToBottom('instant');
      initialScrollDoneRef.current = true;
    }
  }, [messages.length, scrollToBottom, activeChannelId, activeDMThreadId]);

  // Auto-scroll to bottom when new messages arrive (only if already at bottom)
  useEffect(() => {
    if (messages.length > 0 && isAtBottom && initialScrollDoneRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, isAtBottom]);

  // Loading state (initial load)
  if (isLoading) {
    return <MessageSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-text-primary">
            Грешка при зареждане
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
            Не можахме да заредим съобщенията. Опитай отново.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg
              className="mr-2 h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Опитай отново
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!messages || messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-dark-text-primary">
            Няма съобщения
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-dark-text-muted">
            Бъди първият, който ще изпрати съобщение в този канал!
          </p>
        </div>
      </div>
    );
  }

  // Messages list with infinite scroll
  return (
    <>
      {/* Pinned Messages Panel (only for channels, not DMs) */}
      {activeChannelId && <PinnedMessagesPanel channelId={activeChannelId} />}

      {/* Jump-to-unread pill */}
      {firstUnreadMessageId && (
        <button
          type="button"
          onClick={jumpToUnread}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-lg transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          Към новите съобщения
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-6 py-4"
      >
        <div className="space-y-1 max-w-[960px] mx-auto w-full">
          {/* Loading indicator at top when fetching older messages */}
          {isFetchingNextPage && <LoadingSpinner />}

          {/* "Beginning of conversation" indicator */}
          {!hasNextPage && !hasMore && messages.length > 0 && (
            <div className="flex items-center justify-center py-4 mb-4">
              <div className="flex items-center space-x-2 text-gray-400 dark:text-dark-text-muted">
                <div className="h-px w-12 bg-gray-300 dark:bg-dark-border-primary" />
                <span className="text-xs font-medium">Начало на разговора</span>
                <div className="h-px w-12 bg-gray-300 dark:bg-dark-border-primary" />
              </div>
            </div>
          )}

          {/* Message items */}
          {messages.map((message, index) => {
            const prev = index > 0 ? messages[index - 1] : null;
            const isGrouped =
              prev !== null &&
              prev.user_id === message.user_id &&
              !message.reply_to_message_id &&
              !message.forwarded_from_message_id &&
              !prev.forwarded_from_message_id &&
              new Date(message.created_at).getTime() -
                new Date(prev.created_at).getTime() <
                5 * 60 * 1000;

            return (
              <div key={message.id}>
                {firstUnreadMessageId === message.id && (
                  <div
                    ref={unreadDividerRef}
                    className="flex items-center gap-2 py-1.5 my-1"
                    aria-label="Нови съобщения"
                  >
                    <div className="h-px flex-1 bg-red-400/50 dark:bg-red-500/40" />
                    <span className="text-xs font-semibold text-red-500 dark:text-red-400 px-2">
                      Нови съобщения
                    </span>
                    <div className="h-px flex-1 bg-red-400/50 dark:bg-red-500/40" />
                  </div>
                )}
                <MessageItem
                  message={message}
                  channelId={activeChannelId}
                  onReply={onReply}
                  isGrouped={isGrouped && firstUnreadMessageId !== message.id}
                />
              </div>
            );
          })}

          {/* Invisible element to scroll to */}
          <div ref={messagesEndRef} />
        </div>
      </div>
    </>
  );
};

export default MessageList;
